// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import {ColorPickerField} from '@app/features/ui/components/form/ColorPickerField';
import {PaletteIcon} from '@app/features/ui/icons/Icons';
import styles from '@app/features/user/components/modals/tabs/my_profile_tab/AccentColorPicker.module.css';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';

const DEFAULT_PROFILE_ACCENT_COLOR = 0x4641d9;

const ACCENT_COLOR_DESCRIPTOR = msg({
	message: 'Accent color',
	comment: 'Short label in the accent color picker. Keep it concise.',
});
const CUSTOMIZES_THE_BORDER_AND_BANNER_COLOR_ON_YOUR_DESCRIPTOR = msg({
	message: 'Customizes the border and banner color on your profile',
	comment: 'Label in the accent color picker. Keep the tone plain and specific.',
});

interface AccentColorPickerProps {
	value: number | null;
	onChange: (value: number | null) => void;
	disabled?: boolean;
	errorMessage?: string;
	className?: string;
}

export const AccentColorPicker = observer(
	({value, onChange, disabled, errorMessage, className}: AccentColorPickerProps) => {
		const {i18n} = useLingui();
		return (
			<>
				<SettingsRow
					className={className}
					icon={<PaletteIcon size={20} />}
					title={i18n._(ACCENT_COLOR_DESCRIPTOR)}
					description={i18n._(CUSTOMIZES_THE_BORDER_AND_BANNER_COLOR_ON_YOUR_DESCRIPTOR)}
					disabled={disabled}
					control={() => (
						<ColorPickerField
							variant="swatch"
							swatchSize={34}
							swatchLabel={i18n._(ACCENT_COLOR_DESCRIPTOR)}
							value={value ?? DEFAULT_PROFILE_ACCENT_COLOR}
							onChange={onChange}
							defaultValue={DEFAULT_PROFILE_ACCENT_COLOR}
							isDefaultValue={value === null}
							onReset={() => onChange(null)}
							disabled={disabled}
							data-flx="user.my-profile-tab.accent-color-picker.color-picker-field.change"
						/>
					)}
					data-flx="user.my-profile-tab.accent-color-picker.row"
				/>
				{errorMessage && (
					<p className={styles.errorMessage} data-flx="user.my-profile-tab.accent-color-picker.error-message">
						{errorMessage}
					</p>
				)}
			</>
		);
	},
);
